// Translation keys for status names and messages
const STATUS_TRANSLATION_KEYS = {
  awaiting_detection: {
    nameKey: 'widgets.polePriceAudit.statusNames.awaitingDetection',
    messageKey: 'widgets.polePriceAudit.statusMessages.awaitingDetection',
    category: 'no-action',
  },
  cma_submission_pending: {
    nameKey: 'widgets.polePriceAudit.statusNames.cmaSubmissionPending',
    messageKey: 'widgets.polePriceAudit.statusMessages.cmaSubmissionPending',
    category: 'no-action',
  },
  completed: {
    nameKey: 'widgets.polePriceAudit.statusNames.completed',
    messageKey: 'widgets.polePriceAudit.statusMessages.completed',
    category: 'no-action',
  },
  cma_submission_non_compliant_late: {
    nameKey: 'widgets.polePriceAudit.statusNames.cmaSubmissionNonCompliantLate',
    messageKey: 'widgets.polePriceAudit.statusMessages.cmaSubmissionNonCompliantLate',
    category: 'no-action',
  },
  awaiting_detection_amber: {
    nameKey: 'widgets.polePriceAudit.statusNames.awaitingDetectionAmber',
    messageKey: 'widgets.polePriceAudit.statusMessages.awaitingDetectionAmber',
    category: 'potential-action',
  },
  cma_submission_warning: {
    nameKey: 'widgets.polePriceAudit.statusNames.cmaSubmissionWarning',
    messageKey: 'widgets.polePriceAudit.statusMessages.cmaSubmissionWarning',
    category: 'potential-action',
  },
  awaiting_detection_red: {
    nameKey: 'widgets.polePriceAudit.statusNames.awaitingDetectionRed',
    messageKey: 'widgets.polePriceAudit.statusMessages.awaitingDetectionRed',
    category: 'immediate-action',
  },
  cma_submission_non_compliant_retrying: {
    nameKey: 'widgets.polePriceAudit.statusNames.cmaSubmissionNonCompliantRetrying',
    messageKey: 'widgets.polePriceAudit.statusMessages.cmaSubmissionNonCompliantRetrying',
    category: 'immediate-action',
  },
  cma_submission_non_compliant_failed: {
    nameKey: 'widgets.polePriceAudit.statusNames.cmaSubmissionNonCompliantFailed',
    messageKey: 'widgets.polePriceAudit.statusMessages.cmaSubmissionNonCompliantFailed',
    category: 'immediate-action',
  },
}

const ALL_STATUS_KEYS = Object.keys(STATUS_TRANSLATION_KEYS)

const STATUS_FILTER_BY_ACTION_GROUPS = [
  {
    key: 'no-action-group',
    name: 'No Action',
    category: 'no-action',
  },
  {
    key: 'potential-action-group',
    name: 'Potential Action',
    category: 'potential-action',
  },
  {
    key: 'immediate-action-group',
    name: 'Immediate Action',
    category: 'immediate-action',
  },
]

const NON_CMA_STATUS_KEYS = [
  'awaiting_detection',
  'awaiting_detection_amber',
  'awaiting_detection_red',
  'completed',
]

/**
 * Builds an array of status objects from a list of status keys.
 *
 * Each returned object includes the `key` and `messageKey` alongside the translated `name`
 * using the provided translation function.
 *
 * @param {string[]} keys - List of status keys to build.
 * @param {Function} t - Translation function from react-i18next.
 * @returns {{ key: string, name: string, messageKey: string, category: string }[]}
 */
const buildStatusesArray = (keys, t) => {
  return keys.map((key) => {
    const translationKeys = STATUS_TRANSLATION_KEYS[key]
    return {
      key,
      name: t(translationKeys.nameKey),
      messageKey: translationKeys.messageKey,
      category: translationKeys.category,
    }
  })
}

/**
 * Returns the appropriate set of price change statuses.
 *
 * @param {boolean} requiresCmaReporting - Whether pole price changes require CMA reporting (en-GB)
 * @param {Function} t - Translation function from react-i18next
 * @returns {{ key: string, name: string, messageKey: string, category: string }[]}
 */
export const getPriceChangeStatusesForAGivenLocale = (requiresCmaReporting, t) => {
  const statusKeys = requiresCmaReporting ? ALL_STATUS_KEYS : NON_CMA_STATUS_KEYS
  return buildStatusesArray(statusKeys, t)
}

/**
 * Builds the status dropdown structure used by the Pole Price Audit status filter.
 *
 * The returned array contains:
 * - A parent row for each action group (`No Action`, `Potential Action`, `Immediate Action`)
 * - The backend status options that belong to each action group
 *
 * Parent rows are UI-only helpers. They are given:
 * - `showIcon: false` so they do not render a status icon
 * - `toggleItems` containing the child status keys so the dropdown can select or deselect the whole group at once
 *
 * Child rows are the 'real' selectable statuses. They are given `indentLevel: 1`
 * so they render visually nested under their parent group.
 *
 * @param {boolean} requiresCmaReporting - Whether CMA-specific statuses should be included.
 * @param {Function} t - Translation function from react-i18next.
 * @returns {Array<{
 *   key: string,
 *   name: string,
 *   category: string,
 *   messageKey?: string,
 *   indentLevel?: number,
 *   showIcon?: boolean,
 *   toggleItems?: string[]
 * }>}
 */
export const getGroupedPriceChangeStatusesForFilter = (requiresCmaReporting, t) => {
  const allStatuses = getPriceChangeStatusesForAGivenLocale(requiresCmaReporting, t)

  return STATUS_FILTER_BY_ACTION_GROUPS.flatMap(group => {

    // Get the statuses that belong to this action group and add properties needed for rendering in the dropdown
    const groupedStatuses = allStatuses
      .filter(status => status.category === group.category)
      .map(status => ({
        ...status,
        indentLevel: 1,
      }))

    if (groupedStatuses.length === 0) return []

    return [
      {
        ...group,
        showIcon: false,
        toggleItems: groupedStatuses.map(status => status.key),
      },
      ...groupedStatuses,
    ]
  })
}
